import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyGetClassmedOrderStatusQuery } from '../../features/quizApiSlice';
import { getClassmedResultPath, getStoredClassmedId, getStoredClassmedPharmacyId } from '../../utils/classmed';
import { Icon } from '../elements/icon';
import Header from '../Customer/product/Header';
import { Helmet } from 'react-helmet';
import { formatClassmedPrice } from './ClassmedOneOffPurchaseCard';

// Reached only through a personal ClassMED link: keep it out of search results.
const noIndexHelmet = (
  <Helmet>
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 15;

const ClassmedOrderSuccess = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = String(searchParams.get('session_id') || '').trim();
  const [getOrderStatus] = useLazyGetClassmedOrderStatusQuery();
  const [orderStatus, setOrderStatus] = useState(null);
  const [isInvalid, setIsInvalid] = useState(!sessionId);
  const [isTimedOut, setIsTimedOut] = useState(false);

  useEffect(() => {
    i18n.changeLanguage('fr');
  }, [i18n]);

  useEffect(() => {
    if (!sessionId) return undefined;

    let isCancelled = false;
    let timeoutId;
    let attempts = 0;

    const pollOrderStatus = async () => {
      attempts += 1;

      try {
        const response = await getOrderStatus(sessionId, false).unwrap();
        if (isCancelled) return;

        const nextStatus = response?.data || null;
        setOrderStatus(nextStatus);
        if (nextStatus?.status === 'paid') return;
      } catch (error) {
        if (isCancelled) return;

        if ([400, 404].includes(Number(error?.status))) {
          setIsInvalid(true);
          return;
        }
      }

      if (attempts >= MAX_POLL_ATTEMPTS) {
        setIsTimedOut(true);
        return;
      }

      timeoutId = window.setTimeout(pollOrderStatus, POLL_INTERVAL_MS);
    };

    pollOrderStatus();

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [getOrderStatus, sessionId]);

  const isPaid = orderStatus?.status === 'paid';
  const titleKey = isInvalid
    ? 'Classmed_OrderInvalidTitle'
    : isPaid
      ? 'Classmed_OrderSuccessTitle'
      : 'Classmed_OrderProcessingTitle';
  const bodyKey = isInvalid
    ? 'Classmed_OrderInvalidBody'
    : isPaid
      ? 'Classmed_OrderSuccessBody'
      : isTimedOut
        ? 'Classmed_OrderDelayedBody'
        : orderStatus?.status === 'processing'
          ? 'Classmed_OrderProcessingPaidBody'
          : 'Classmed_OrderProcessingBody';
  const resultPath = useMemo(
    () => getClassmedResultPath(getStoredClassmedId(), { pharmacyId: getStoredClassmedPharmacyId() }),
    [],
  );

  return (
    <main className="t-min-h-[100dvh] t-bg-surface t-font-sans t-text-navy">
      {noIndexHelmet}
      <Header showLanguageSelector={false} homePath={resultPath} />
      <section className="t-mx-auto t-flex t-min-h-[calc(100dvh-52px)] t-w-full t-max-w-[620px] t-flex-col t-items-center t-justify-center t-px-5 t-py-12 t-text-center">
        <div
          className={`t-flex t-h-16 t-w-16 t-items-center t-justify-center t-rounded-full ${
            isPaid ? 't-bg-quaternary/15' : 't-bg-navy/10'
          }`}
        >
          {isPaid ? (
            <Icon name="tealCheckCircle" size={46} color="#116D6E" raw />
          ) : isInvalid ? (
            <Icon name="whyCircle" size={32} color="#172448" raw />
          ) : (
            <span className="t-h-7 t-w-7 t-animate-spin t-rounded-full t-border-[3px] t-border-navy/20 t-border-t-navy" />
          )}
        </div>

        <h1 className="!t-m-0 !t-mt-6 t-font-serif t-text-3xl t-font-normal t-text-navy sm:t-text-4xl">
          {t(titleKey)}
        </h1>
        <p className="!t-m-0 !t-mt-3 t-max-w-[520px] t-text-base t-leading-relaxed t-text-navy/70">
          {t(bodyKey)}
        </p>

        {!isInvalid && orderStatus && (
          <dl className="t-m-0 t-mt-8 t-w-full t-border-y t-border-navy/15 t-py-2 t-text-left">
            {orderStatus.orderReference && (
              <div className="t-flex t-items-center t-justify-between t-gap-4 t-border-b t-border-navy/10 t-py-3">
                <dt className="t-text-sm t-text-navy/60">{t('Classmed_OrderReference')}</dt>
                <dd className="t-m-0 t-text-sm t-font-semibold t-text-navy">
                  {orderStatus.orderReference}
                </dd>
              </div>
            )}
            {orderStatus.duration > 0 && (
              <div className="t-flex t-items-center t-justify-between t-gap-4 t-border-b t-border-navy/10 t-py-3">
                <dt className="t-text-sm t-text-navy/60">{t('Classmed_OrderDuration')}</dt>
                <dd className="t-m-0 t-text-sm t-font-semibold t-text-navy">
                  {orderStatus.duration} {t('Classmed_OrderDays')}
                </dd>
              </div>
            )}
            {orderStatus.paidAmount != null && (
              <div className="t-flex t-items-center t-justify-between t-gap-4 t-py-3">
                <dt className="t-text-sm t-text-navy/60">{t('Classmed_OrderPaidAmount')}</dt>
                <dd className="t-m-0 t-text-sm t-font-semibold t-text-navy">
                  {formatClassmedPrice(orderStatus.paidAmount)}
                </dd>
              </div>
            )}
          </dl>
        )}

        {isPaid && (
          <p className="!t-m-0 !t-mt-6 t-text-sm t-leading-relaxed t-text-navy/70">
            {t('Classmed_OrderFulfillment')}
          </p>
        )}

        <a
          href={resultPath}
          className="!t-mt-8 t-inline-flex t-items-center t-justify-center t-rounded-full t-bg-navy t-px-6 t-py-3 t-text-sm t-font-semibold t-text-white hover:t-text-white hover:t-opacity-90"
        >
          {t('Classmed_OrderBackToResult')}
        </a>
      </section>
    </main>
  );
};

export default ClassmedOrderSuccess;
